import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { useAppSelector } from '@/hooks/useAppSelector';
import {
    USER_ACTIVITY_EVENTS,
    USER_ACTIVITY_THROTTLE_MS,
    clearSharedLastActivityAt,
    formatIdleWarningLead,
    idleWarningLeadMs,
    isUserIdleActivity,
    msUntilIdleLogout,
    msUntilIdleWarning,
    readSharedLastActivityAt,
    resolveInactivityTimeoutMs,
    writeSharedLastActivityAt,
    LAST_ACTIVITY_STORAGE_KEY
} from '@/services/idle-timeout';
import { logoutSession } from '@/services/session-logout';
import axiosInstance from '@/utils/axios';

type SessionConfigResponse = {
    inactivityTimeoutMinutes?: number;
};

/**
 * Signs the user out after the server-configured inactivity window.
 * Pointer, keyboard, wheel, touch, and scroll events reset the clock.
 * Background calls such as /auth/refresh do not.
 */
const IdleSessionMonitor = () => {
    const isLoggedIn = useAppSelector(({ auth }) => auth.isLoggedIn);
    const [warningLeadLabel, setWarningLeadLabel] = useState('2 minutes');
    const [warningOpen, setWarningOpen] = useState(false);
    const markActivityRef = useRef<() => void>(() => undefined);

    useEffect(() => {
        if (!isLoggedIn) {
            setWarningOpen(false);
            return undefined;
        }

        let warningTimer = 0;
        let logoutTimer = 0;
        let cancelled = false;
        let loggingOut = false;
        const timeoutMsRef = { current: resolveInactivityTimeoutMs(undefined) };
        const lastActivityRef = { current: Date.now() };

        const clearTimers = () => {
            window.clearTimeout(warningTimer);
            window.clearTimeout(logoutTimer);
        };

        const expire = () => {
            if (loggingOut || cancelled) {
                return;
            }

            const sharedActivityAt = readSharedLastActivityAt();
            if (sharedActivityAt != null && sharedActivityAt > lastActivityRef.current) {
                lastActivityRef.current = sharedActivityAt;
            }

            if (msUntilIdleLogout(lastActivityRef.current, Date.now(), timeoutMsRef.current) > 0) {
                arm();
                return;
            }

            loggingOut = true;
            clearTimers();
            setWarningOpen(false);
            clearSharedLastActivityAt();
            void logoutSession();
        };

        const arm = () => {
            clearTimers();
            const now = Date.now();
            const timeoutMs = timeoutMsRef.current;
            const untilLogout = msUntilIdleLogout(lastActivityRef.current, now, timeoutMs);
            if (untilLogout <= 0) {
                expire();
                return;
            }

            const untilWarning = msUntilIdleWarning(lastActivityRef.current, now, timeoutMs);
            if (untilWarning <= 0) {
                setWarningLeadLabel(formatIdleWarningLead(idleWarningLeadMs(timeoutMs)));
                setWarningOpen(true);
            } else {
                setWarningOpen(false);
                warningTimer = window.setTimeout(() => {
                    setWarningLeadLabel(formatIdleWarningLead(idleWarningLeadMs(timeoutMsRef.current)));
                    setWarningOpen(true);
                }, untilWarning);
            }

            logoutTimer = window.setTimeout(expire, untilLogout);
        };

        const markActivity = (force = false) => {
            if (loggingOut) {
                return;
            }
            const now = Date.now();
            if (!force && now - lastActivityRef.current < USER_ACTIVITY_THROTTLE_MS) {
                return;
            }
            lastActivityRef.current = now;
            writeSharedLastActivityAt(now);
            arm();
        };

        markActivityRef.current = () => markActivity(true);
        lastActivityRef.current = Date.now();
        writeSharedLastActivityAt(lastActivityRef.current);
        arm();

        const onActivity = (event: Event) => {
            if (!isUserIdleActivity(event.type)) {
                return;
            }
            markActivity();
        };

        USER_ACTIVITY_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, onActivity, { capture: true, passive: true });
        });

        const onStorage = (event: StorageEvent) => {
            if (event.key !== LAST_ACTIVITY_STORAGE_KEY || !event.newValue) {
                return;
            }
            const next = Number(event.newValue);
            if (!Number.isFinite(next) || next <= lastActivityRef.current) {
                return;
            }
            lastActivityRef.current = next;
            arm();
        };

        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                arm();
            }
        };

        window.addEventListener('storage', onStorage);
        document.addEventListener('visibilitychange', onVisible);

        axiosInstance
            .get<SessionConfigResponse>('/auth/session-config')
            .then((response) => {
                if (cancelled) {
                    return;
                }
                timeoutMsRef.current = resolveInactivityTimeoutMs(response.data?.inactivityTimeoutMinutes);
                arm();
            })
            .catch(() => {
                // Keep the 15-minute default when session config cannot be loaded.
            });

        return () => {
            cancelled = true;
            clearTimers();
            markActivityRef.current = () => undefined;
            USER_ACTIVITY_EVENTS.forEach((eventName) => {
                window.removeEventListener(eventName, onActivity, { capture: true });
            });
            window.removeEventListener('storage', onStorage);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [isLoggedIn]);

    if (!isLoggedIn) {
        return null;
    }

    return (
        <Dialog open={warningOpen} fullWidth maxWidth="xs">
            <DialogTitle>Session expiring</DialogTitle>
            <DialogContent>
                <Typography variant="body2">
                    You will be signed out in {warningLeadLabel} because of inactivity. Any unsaved work may be lost.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={markActivityRef.current}>
                    Stay signed in
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default IdleSessionMonitor;
